/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptLocale from '@fullcalendar/core/locales/pt';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/fr';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DownloadIcon,
  Loader2Icon,
  PlusIcon,
  SettingsIcon,
  UploadIcon,
} from 'lucide-react';
import {
  Accordion as ShadAccordion,
  AccordionContent as ShadAccordionContent,
  AccordionItem as ShadAccordionItem,
  AccordionTrigger as ShadAccordionTrigger,
} from '@/components/ui/accordion';
import { Badge as ShadBadge } from '@/components/ui/badge';
import { Button as ShadButton } from '@/components/ui/button';
import { Card as ShadCard, CardContent as ShadCardContent } from '@/components/ui/card';
import { Checkbox as ShadCheckbox } from '@/components/ui/checkbox';
import { ScrollArea as ShadScrollArea } from '@/components/ui/scroll-area';
import { Separator as ShadSeparator } from '@/components/ui/separator';
import { Switch as ShadSwitch } from '@/components/ui/switch';
import {
  ToggleGroup as ShadToggleGroup,
  ToggleGroupItem as ShadToggleGroupItem,
} from '@/components/ui/toggle-group';
import { useNotificationsProvider } from '../../context/NotificationsContext';
import './inspections-calendar.css';
import {
  Command as ShadCommand,
  CommandEmpty as ShadCommandEmpty,
  CommandGroup as ShadCommandGroup,
  CommandInput as ShadCommandInput,
  CommandItem as ShadCommandItem,
  CommandList as ShadCommandList,
  CommandSeparator as ShadCommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover as ShadPopover,
  PopoverContent as ShadPopoverContent,
  PopoverTrigger as ShadPopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip as ShadTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn as shadCn } from '@/lib/utils';


// Helper to get locale object (handle both default export and direct export)
const getLocaleObject = (locale) => {
  if (!locale) return null;
  // Handle ES module default export
  if (locale.default && typeof locale.default === 'object') {
    return locale.default;
  }
  // Handle direct export
  if (locale.code) {
    return locale;
  }
  return locale;
};

// Get locale code for Intl API
const getLocaleCode = (language) => {
  if (language === 'pt') return 'pt-PT';
  if (language === 'fr') return 'fr-FR';
  return 'en-GB';
};

// Neutral color for controllers without an assigned color
const UNASSIGNED_COLOR = '#757575';

// Special ID for unassigned filter option
const UNASSIGNED_FILTER_ID = '__unassigned__';

// Color for overdue (past + not completed) events
const OVERDUE_COLOR = '#d32f2f';

// Conformity values (matching backend)
const CONFORMITY = {
  NOT_OK: 0,
  OK: 1,
  WAITING_RESULT: 2,
  DOES_NOT_APPLY: 3,
};

// Control status values (matching backend)
const CONTROL_STATUS = {
  ACTIVE: 1,
  CANCELED: 2,
};

// Helper to check if an event date is in the past
const isEventPast = (eventDate) => {
  const today = moment().startOf('day');
  const eventDay = moment(eventDate).startOf('day');
  return eventDay.isBefore(today);
};

// Helper to check if a date is today or in the future
const isDateTodayOrFuture = (date) => {
  const today = moment().startOf('day');
  const targetDay = moment(date).startOf('day');
  return targetDay.isSameOrAfter(today);
};

// Helper to check if an event is completed (has a result)
const isEventCompleted = (conformity) =>
  // Completed if conformity is not WAITING_RESULT (2) and not null/undefined
  conformity !== null &&
  conformity !== undefined &&
  conformity !== CONFORMITY.WAITING_RESULT
  ;

// Helper to convert hex to rgba with opacity
const hexToRgba = (hex, opacity) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

InspectionsCalendar.propTypes = {
  title: PropTypes.string,
  fetchEventsFunction: PropTypes.func.isRequired,
  fetchControllersFunction: PropTypes.func, // () => Promise<{ controllers: [...] }>
  updateControllerColorFunction: PropTypes.func, // (userId, color) => Promise<{ user: {...} }>
  updateControllerVisibilityFunction: PropTypes.func, // (userId, visible) => Promise<{ user: {...} }>
  updateEventDateFunction: PropTypes.func, // (type, id, newDate) => Promise<{ event: {...} }>
  cancelEventFunction: PropTypes.func, // (type, id) => Promise<{ event: {...} }> - cancel behind schedule control
  assignControllerFunction: PropTypes.func, // (type, id, controllerId) => Promise<{ control: {...} }> - assign controller to control
  exportCSVFunction: PropTypes.func, // (events, { t, selectedDate, isEventPast, isEventCompleted, CONTROL_STATUS }) => void - custom CSV export
  onError: PropTypes.func, // (message) => void - callback for error handling (e.g., show notification)
  onControlClick: PropTypes.func,
  onDateClick: PropTypes.func,
  onCreateControl: PropTypes.func, // (date) => void - callback to create a new control for the selected date
  memberId: PropTypes.number,
  height: PropTypes.string,
  itemLabelSingular: PropTypes.string,
  itemLabelPlural: PropTypes.string,
  calendarSx: PropTypes.object,
  renderControlDetails: PropTypes.func, // (control, { onClose, onViewFullDetails, onCancel }) => ReactNode
  renderEventListItem: PropTypes.func, // (event, { controllerColors, t }) => { primary: ReactNode, secondary: ReactNode } - custom list item content
  getEventTitle: PropTypes.func, // (event, { itemLabel }) => string - custom event title (default: controller name)
  showControllerFilter: PropTypes.bool, // Show/hide the controller filter (default: true)
  canRescheduleEvents: PropTypes.bool, // Allow drag and drop to reschedule events (default: true)
  canCancelOverdueEvents: PropTypes.bool, // Allow canceling overdue events (default: true)
  canCreateControl: PropTypes.bool, // Allow creating controls from the calendar (default: true if onCreateControl is provided)
  canAssignController: PropTypes.bool, // Allow assigning controllers to unassigned controls (default: true if assignControllerFunction is provided)
  // Controlled state props for URL persistence
  defaultView: PropTypes.string, // Initial view type ('dayGridMonth', 'timeGridWeek', 'timeGridDay')
  onViewChange: PropTypes.func, // (viewName) => void - callback when view changes
  defaultDate: PropTypes.instanceOf(Date), // Initial date to display
  onDateChange: PropTypes.func, // (date) => void - callback when displayed date changes
  defaultSelectedControllers: PropTypes.array, // Initial selected controller IDs
  onSelectedControllersChange: PropTypes.func, // (controllerIds) => void - callback when selected controllers change
  onRefreshRef: PropTypes.func, // (refreshFn) => void - callback to receive a refresh function for external use
  onImportCalendar: PropTypes.func, // () => void - callback when user clicks "Import calendar" button
  onExportTemplate: PropTypes.func, // () => void - callback when user clicks "Export template" button
};

/**
 * InspectionsCalendar - A calendar component for displaying controls/inspections
 *
 * @param {function} fetchEventsFunction - Function to fetch events.
 *   Should accept (params) and return a Promise that resolves to { events: [...] }
 *   params: { startDate, endDate, controllerIds?, memberId? }
 */
export default function InspectionsCalendar({
  title = '',
  fetchEventsFunction,
  fetchControllersFunction = null,
  updateControllerColorFunction = null,
  updateControllerVisibilityFunction = null,
  updateEventDateFunction = null,
  cancelEventFunction = null,
  assignControllerFunction = null,
  exportCSVFunction = null,
  onError = null,
  onControlClick,
  onDateClick,
  onCreateControl = null,
  memberId = null,
  height = '600px',
  itemLabelSingular = null,
  itemLabelPlural = null,
  calendarSx = {},
  renderControlDetails = null,
  renderEventListItem = null,
  getEventTitle = null,
  showControllerFilter = true,
  canRescheduleEvents = true,
  canCancelOverdueEvents = true,
  canCreateControl = true,
  canAssignController = true,
  defaultView = 'dayGridMonth',
  onViewChange = null,
  defaultDate = null,
  onDateChange = null,
  defaultSelectedControllers = null,
  onSelectedControllersChange = null,
  onRefreshRef = null,
  onImportCalendar = null,
  onExportTemplate = null,
}) {
  // Brand accents (active button states, focused borders, etc.) come
  // from the shadcn theme tokens — `--primary` is wired to the
  // Betterfield blue in `src/index.css`. `mainColor` was the legacy
  // hex passthrough that the now-deleted MUI `sx` props read; carry a
  // CSS-var equivalent so existing string-based consumers (e.g. the
  // `renderControlDetails` callback's `controllerColors` fallback)
  // keep working.
  const mainColor = 'var(--primary)';
  const { t, i18n } = useTranslation();
  const { showNotification } = useNotificationsProvider();
  const calendarRef = useRef(null);

  // Store initial values in refs so the FullCalendar key doesn't change on user interaction
  const initialViewRef = useRef(defaultView);
  const initialDateRef = useRef(defaultDate);

  // Normalize language code for moment.js
  const normalizedLangForMoment = i18n.language?.split('-')[0] || 'en';

  // Set moment locale on initial render
  if (normalizedLangForMoment === 'pt') {
    moment.locale('pt');
  } else if (normalizedLangForMoment === 'fr') {
    moment.locale('fr');
  } else {
    moment.locale('en');
  }

  // Get FullCalendar locale based on i18n language
  const getFullCalendarLocale = useCallback(() => {
    // Normalize language code (handle variants like 'pt-BR', 'pt-PT', etc.)
    const normalizedLang = i18n.language?.split('-')[0] || 'en';

    const localeObj = normalizedLang === 'pt'
      ? getLocaleObject(ptLocale)
      : normalizedLang === 'fr'
        ? getLocaleObject(frLocale)
        : getLocaleObject(enLocale);

    if (!localeObj) return null;

    // FullCalendar v6 uses browser's Intl API for month/day names
    // But we need to provide them explicitly for proper localization
    const localeCode = getLocaleCode(normalizedLang);

    // Create formatters
    const dateFormatter = new Intl.DateTimeFormat(localeCode, { month: 'long' });
    const dayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'long' });
    const shortDayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'short' });
    const shortMonthFormatter = new Intl.DateTimeFormat(localeCode, { month: 'short' });
    const narrowDayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'narrow' });

    // Generate month names (full and short)
    const monthNames = [];
    const monthNamesShort = [];
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(2024, i, 1);
      monthNames.push(dateFormatter.format(date));
      monthNamesShort.push(shortMonthFormatter.format(date));
    }

    // Generate day names starting from the locale's first day of week
    const dayNames = [];
    const dayNamesShort = [];
    const dayNamesMin = [];
    const firstDayOfWeek = localeObj.week?.dow || 1; // Default to Monday (1)

    // Start from a known Monday (January 1, 2024 is a Monday)
    const baseDate = new Date(2024, 0, 1);
    for (let i = 0; i < 7; i += 1) {
      const dayOffset = (firstDayOfWeek + i) % 7;
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + dayOffset);
      dayNames.push(dayFormatter.format(date));
      dayNamesShort.push(shortDayFormatter.format(date));
      dayNamesMin.push(narrowDayFormatter.format(date));
    }

    // FullCalendar v6 expects these properties for localization
    const enhancedLocale = {
      ...localeObj,
      monthNames,
      monthNamesShort,
      dayNames,
      dayNamesShort,
      dayNamesMin,
      code: localeObj.code || normalizedLang
    };

    return enhancedLocale;
  }, [i18n.language]);

  // Set moment locale and update FullCalendar locale based on i18n language
  useEffect(() => {
    // Normalize language code
    const normalizedLang = i18n.language?.split('-')[0] || 'en';

    // Set moment locale
    if (normalizedLang === 'pt') {
      moment.locale('pt');
    } else if (normalizedLang === 'fr') {
      moment.locale('fr');
    } else {
      moment.locale('en');
    }

    // Update FullCalendar locale when language changes
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const locale = getFullCalendarLocale();
      calendarApi.setOption('locale', locale);
      // Force a rerender to apply locale changes
      calendarApi.render();
    }
  }, [i18n.language, getFullCalendarLocale]);

  const [view, setViewState] = useState(defaultView || 'dayGridMonth');
  const [currentDate, setCurrentDateState] = useState(defaultDate || new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controllers, setControllers] = useState([]);
  const [selectedControllersState, setSelectedControllersState] = useState(defaultSelectedControllers || []);

  // Sync FullCalendar with defaultView and defaultDate when they change (e.g., from URL params)
  const initialSyncDoneRef = useRef(false);
  useEffect(() => {
    if (calendarRef.current && !initialSyncDoneRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (defaultView && defaultView !== calendarApi.view.type) {
        calendarApi.changeView(defaultView);
        setViewState(defaultView);
      }
      if (defaultDate) {
        calendarApi.gotoDate(defaultDate);
        setCurrentDateState(defaultDate);
      }
      initialSyncDoneRef.current = true;
    }
  }, [defaultView, defaultDate]);

  // Wrapper functions that also call callback props for URL persistence
  const setView = useCallback((newView) => {
    setViewState(newView);
    if (onViewChange) {
      onViewChange(newView);
    }
  }, [onViewChange]);

  const setCurrentDate = useCallback((newDate) => {
    setCurrentDateState(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  }, [onDateChange]);

  const setSelectedControllers = useCallback((newSelection) => {
    setSelectedControllersState(newSelection);
    if (onSelectedControllersChange) {
      onSelectedControllersChange(newSelection);
    }
  }, [onSelectedControllersChange]);

  // Use the state value for selectedControllers
  const selectedControllers = selectedControllersState;
  const [controllerColors, setControllerColors] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [controllerFilterOpen, setControllerFilterOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [selectedControl, setSelectedControl] = useState(null);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [tempControllerColors, setTempControllerColors] = useState({});
  const [allControllers, setAllControllers] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [tempControllerVisibility, setTempControllerVisibility] = useState({});
  const [expandedEventId, setExpandedEventId] = useState(null);

  // Refs to track filter state (avoids dependency loops in fetchEvents)
  const selectedControllersRef = useRef(defaultSelectedControllers || []);
  const allControllersCountRef = useRef(0);
  // Counter to trigger filter refetch
  const [filterTrigger, setFilterTrigger] = useState(0);

  // Helper to process fetched events - extracts controllers from events for legend
  const processEventsData = useCallback((data) => {
    setEvents(data.events || []);
    // Extract unique controllers and their colors from events (for legend/badges)
    const uniqueControllers = new Map();
    const colorsFromEvents = {};
    (data.events || []).forEach(event => {
      if (event.controller && event.controller.id) {
        if (!uniqueControllers.has(event.controller.id)) {
          uniqueControllers.set(event.controller.id, event.controller);
          if (event.controller.color) {
            colorsFromEvents[event.controller.id] = event.controller.color;
          }
        }
      }
    });
    // Controllers from events are used for the legend/badges
    setControllers(Array.from(uniqueControllers.values()));
    // Update controller colors
    if (Object.keys(colorsFromEvents).length > 0) {
      setControllerColors(prev => ({ ...prev, ...colorsFromEvents }));
    }
    setLoading(false);
  }, []);

  // Fetch calendar events with current filter
  const fetchEvents = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const startDate = moment(start).startOf('day').toISOString();
      const endDate = moment(end).endOf('day').toISOString();

      const params = {
        startDate,
        endDate,
      };

      // Apply controller filter using refs (to avoid dependency loop)
      const selected = selectedControllersRef.current;
      const totalControllers = allControllersCountRef.current;
      // +1 for the unassigned option
      const totalOptions = totalControllers + 1;

      // Check if unassigned is selected
      const includeUnassigned = selected.includes(UNASSIGNED_FILTER_ID);
      // Get actual controller IDs (excluding the special unassigned ID)
      const controllerIds = selected.filter(id => id !== UNASSIGNED_FILTER_ID);

      // Only apply filter when some options are selected (not none and not all)
      // None selected = show all (no filter)
      // All selected = show all (no filter)
      // Some selected = filter by those
      const noneSelected = selected.length === 0;
      const allSelected = selected.length === totalOptions;

      if (!noneSelected && !allSelected) {
        // Some selected - apply filter
        if (controllerIds.length > 0) {
          params.controllerIds = controllerIds;
        }
        if (includeUnassigned) {
          params.includeUnassigned = true;
        }
        // If only unassigned selected (no controllers)
        if (controllerIds.length === 0 && includeUnassigned) {
          params.unassignedOnly = true;
          delete params.includeUnassigned;
        }
      }
      // If none selected or all selected, don't filter (show all)

      if (memberId) {
        params.memberId = memberId;
      }

      const data = await fetchEventsFunction(params);
      processEventsData(data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setLoading(false);
    }
  }, [memberId, fetchEventsFunction, processEventsData]);

  // Track if controllers have been loaded
  const [controllersLoaded, setControllersLoaded] = useState(false);

  // Expose refresh function to parent via callback
  useEffect(() => {
    if (onRefreshRef) {
      const refreshFn = () => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          const start = calendarApi.view.activeStart;
          const end = calendarApi.view.activeEnd;
          fetchEvents(start, end);
        }
      };
      onRefreshRef(refreshFn);
    }
  }, [onRefreshRef, fetchEvents]);

  // Fetch all controllers on mount for the filter dropdown
  useEffect(() => {
    const loadAllControllers = async () => {
      if (fetchControllersFunction) {
        try {
          const data = await fetchControllersFunction();
          const controllersList = data.controllers || [];
          setAllControllers(controllersList);
          allControllersCountRef.current = controllersList.length;
          // Load colors from backend
          const colorsFromBackend = {};
          controllersList.forEach(controller => {
            if (controller.color) {
              colorsFromBackend[controller.id] = controller.color;
            }
          });
          if (Object.keys(colorsFromBackend).length > 0) {
            setControllerColors(prev => ({ ...colorsFromBackend, ...prev }));
          }
          setControllersLoaded(true);
        } catch (err) {
          console.error('Error fetching all controllers:', err);
        }
      }
    };
    loadAllControllers();
  }, [fetchControllersFunction]);

  // Apply defaultSelectedControllers when controllers are loaded
  useEffect(() => {
    if (!controllersLoaded && fetchControllersFunction) {
      // Wait for controllers to load
      return;
    }

    // Apply the default selection from URL
    if (defaultSelectedControllers && defaultSelectedControllers.length > 0) {
      // Only update if different from current selection
      const currentStr = JSON.stringify([...selectedControllersRef.current].sort());
      const newStr = JSON.stringify([...defaultSelectedControllers].sort());
      if (currentStr !== newStr) {
        selectedControllersRef.current = defaultSelectedControllers;
        setSelectedControllersState(defaultSelectedControllers);
        setFilterTrigger(prev => prev + 1);
      }
    } else {
      // null or empty array means no filter - show all (empty selection)
      selectedControllersRef.current = [];
      setSelectedControllersState([]);
    }
  }, [controllersLoaded, defaultSelectedControllers, fetchControllersFunction]);

  // Fetch events when date range or view changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const start = calendarApi.view.activeStart;
      const end = calendarApi.view.activeEnd;
      fetchEvents(start, end);
    }
  }, [currentDate, view, fetchEvents]);

  // Refetch events when filter trigger changes (user-initiated filter change)
  useEffect(() => {
    if (filterTrigger > 0 && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const start = calendarApi.view.activeStart;
      const end = calendarApi.view.activeEnd;
      fetchEvents(start, end);
    }
  }, [filterTrigger, fetchEvents]);

  // Handle filter changes - update ref and trigger refetch
  const handleFilterChange = useCallback((newSelection) => {
    selectedControllersRef.current = newSelection;
    setSelectedControllers(newSelection);
    // Trigger refetch
    setFilterTrigger(prev => prev + 1);
  }, [setSelectedControllers]);

  // Get item label based on count (uses props if provided, otherwise falls back to translations)
  const getItemLabel = useCallback((count) => {
    if (count === 1) {
      return itemLabelSingular || t('calendar.item-singular', { defaultValue: 'item' });
    }
    return itemLabelPlural || t('calendar.item-plural', { defaultValue: 'items' });
  }, [itemLabelSingular, itemLabelPlural, t]);

  // Convert events to FullCalendar format
  const calendarEvents = useMemo(() => events.map(event => {
    const controllerName = event.controller
      ? `${event.controller.firstName} ${event.controller.lastName}`
      : t('calendar.unassigned');
    const itemText = getItemLabel(event.vehicleCount);
    const baseColor = event.controller ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR) : UNASSIGNED_COLOR;

    // Check if event has time (for timed events vs all-day events)
    const hasTime = event.dateTime && event.time && event.time !== '00:00:00';

    // Check if event is canceled
    const isCanceled = event.status === CONTROL_STATUS.CANCELED;

    // Check if event is in the past and its completion status
    const eventDate = hasTime ? event.dateTime : event.date;
    const isPast = isEventPast(eventDate);
    const isCompleted = isEventCompleted(event.conformity);
    // Canceled events are NOT considered overdue
    const isOverdue = isPast && !isCompleted && !isCanceled;

    // Determine the display color based on status
    let displayColor = baseColor;
    let textColor = '#ffffff';

    if (isOverdue) {
      // Past + not completed + not canceled: use overdue color with transparency
      displayColor = hexToRgba(OVERDUE_COLOR, 0.5);
      textColor = '#b71c1c'; // Dark red text for better contrast
    }

    // Build title - use custom getEventTitle if provided, otherwise use controller name
    // Status is indicated visually through colors, not text prefixes
    const customTitle = getEventTitle ? getEventTitle(event, { itemLabel: itemText }) : null;
    const title = hasTime
      ? (customTitle || controllerName)
      : `${customTitle || controllerName} - ${event.vehicleCount} ${itemText}`;

    return {
      id: event.id,
      title,
      start: hasTime ? event.dateTime : event.date,
      end: hasTime ? moment(event.dateTime).add(30, 'minutes').toISOString() : moment(event.date).endOf('day').toISOString(),
      allDay: !hasTime,
      editable: event.editable === true && !isCanceled, // Canceled events are not editable
      extendedProps: {
        ...event,
        controllerName,
        color: baseColor,
        isPast,
        isCompleted,
        isOverdue,
        isCanceled
      },
      backgroundColor: displayColor,
      borderColor: isOverdue ? OVERDUE_COLOR : displayColor,
      textColor
    };
  }), [events, controllerColors, t, getItemLabel, getEventTitle]);

  // Handle event click - show control details modal or day list based on context
  const handleEventClick = (clickInfo) => {
    const eventData = clickInfo.event.extendedProps;
    const currentViewType = clickInfo.view.type;

    // Check if event has a specific time (CATP) or is aggregated (Betterfield)
    // Aggregated events don't have a specific time or have time as '00:00:00'
    const hasSpecificTime = eventData.time && eventData.time !== '00:00:00';

    // For aggregated events (Betterfield) with multiple resources, show list first
    if (!hasSpecificTime && eventData.vehicleCount > 1) {
      // Show day events dialog with this event's resources
      setSelectedDate(clickInfo.event.start);
      setDayEvents([eventData]);
      setDayDialogOpen(true);
      return;
    }

    // In month view with multiple aggregated events on same day, show day list first
    if (currentViewType === 'dayGridMonth' && !hasSpecificTime) {
      const dateStr = eventData.date || moment(clickInfo.event.start).format('YYYY-MM-DD');
      const dayEventsList = events.filter(e => e.date === dateStr);

      if (dayEventsList.length > 1) {
        // Multiple aggregated events - show day events list
        setSelectedDate(clickInfo.event.start);
        setDayEvents(dayEventsList);
        setDayDialogOpen(true);
        return;
      }
    }

    // Single event, timed event, or week/day view - show details directly
    if (renderControlDetails) {
      setSelectedControl(eventData);
      setControlDialogOpen(true);
    } else if (onControlClick) {
      onControlClick(eventData);
    }
  };

  // Close control details modal
  const handleCloseControlDetails = () => {
    setControlDialogOpen(false);
    setSelectedControl(null);
  };

  // Navigate to full control details page
  const handleViewFullDetails = () => {
    if (selectedControl && onControlClick) {
      setControlDialogOpen(false);
      setDayDialogOpen(false); // Also close list when navigating
      onControlClick(selectedControl);
    }
  };

  // Handle event drop (drag and drop rescheduling)
  const handleEventDrop = useCallback(async (dropInfo) => {
    const { event, revert } = dropInfo;
    const eventData = event.extendedProps;

    // Check if update function is provided
    if (!updateEventDateFunction) {
      revert();
      return;
    }

    try {
      // Call the update function with type, id, and new date
      await updateEventDateFunction(
        eventData.type,
        eventData.controlId,
        event.start.toISOString()
      );

      // Update local event data
      const updatedEvents = events.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            date: moment(event.start).format('YYYY-MM-DD'),
            dateTime: event.start.toISOString()
          };
        }
        return e;
      });
      setEvents(updatedEvents);
    } catch (error) {
      console.error('Failed to update event date:', error);
      // Revert the drag if update fails
      revert();
      // Refetch events to reset calendar state - ensures subsequent drags work
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const start = calendarApi.view.activeStart;
        const end = calendarApi.view.activeEnd;
        fetchEvents(start, end);
      }
      // Get error code and translate it
      const errorCode = error?.response?.data?.errorCode;
      const errorMessage = errorCode
        ? t(`calendar.drag-drop.errors.${errorCode}`, { defaultValue: t('calendar.drag-drop.error') })
        : t('calendar.drag-drop.error');
      if (onError) {
        onError(errorMessage);
      } else {
        showNotification('error', errorMessage);
      }
    }
  }, [updateEventDateFunction, events, t, onError, fetchEvents, showNotification]);

  // Handle cancel event (for behind schedule controls)
  const handleCancelEvent = useCallback(async (eventData) => {
    if (!cancelEventFunction) {
      return;
    }

    try {
      await cancelEventFunction(eventData.type, eventData.controlId);

      // Update local event data to mark as canceled
      // Match by controlId since eventData may come from vehicles array which has controlId, not id
      const targetControlId = eventData.controlId;
      const updatedEvents = events.map(e => {
        if (e.controlId === targetControlId) {
          return {
            ...e,
            status: CONTROL_STATUS.CANCELED,
            // Also update vehicles array if present
            vehicles: e.vehicles?.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        return e;
      });
      setEvents(updatedEvents);

      // Also update dayEvents if dialog is open
      setDayEvents(prev => prev.map(e => {
        if (e.controlId === targetControlId) {
          return {
            ...e,
            status: CONTROL_STATUS.CANCELED,
            vehicles: e.vehicles?.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        // Also check inside vehicles array for multi-vehicle events
        if (e.vehicles?.some(v => v.controlId === targetControlId)) {
          return {
            ...e,
            vehicles: e.vehicles.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        return e;
      }));

      // Update selectedControl if it's the one being canceled
      if (selectedControl && selectedControl.controlId === targetControlId) {
        setSelectedControl(prev => ({
          ...prev,
          status: CONTROL_STATUS.CANCELED
        }));
      }

      showNotification('success', t('calendar.cancel.success'));
    } catch (error) {
      console.error('Failed to cancel event:', error);
      const errorCode = error?.response?.data?.errorCode;
      const errorMessage = errorCode
        ? t(`calendar.cancel.errors.${errorCode}`, { defaultValue: t('calendar.cancel.error') })
        : t('calendar.cancel.error');
      if (onError) {
        onError(errorMessage);
      } else {
        showNotification('error', errorMessage);
      }
    }
  }, [cancelEventFunction, events, t, onError, selectedControl]);

  // Handle assign controller to a pending control
  const handleAssignController = useCallback(async (eventData, controllerId) => {
    if (!assignControllerFunction) {
      return;
    }

    try {
      const result = await assignControllerFunction(eventData.type, eventData.controlId, controllerId);

      // Find the assigned controller from allControllers
      const assignedController = allControllers.find(c => c.id === controllerId);

      // Update local event data with the assigned controller
      const updatedEvents = events.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            controllerId,
            controller: assignedController || result?.control?.controller
          };
        }
        return e;
      });
      setEvents(updatedEvents);

      // Also update dayEvents if dialog is open
      setDayEvents(prev => prev.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            controllerId,
            controller: assignedController || result?.control?.controller
          };
        }
        return e;
      }));

      // Update selectedControl if it's the one being assigned
      if (selectedControl && selectedControl.id === eventData.id) {
        setSelectedControl(prev => ({
          ...prev,
          controllerId,
          controller: assignedController || result?.control?.controller
        }));
      }

      showNotification('success', t('calendar.assign.success'));
    } catch (error) {
      console.error('Failed to assign controller:', error);
      const errorMessage = t('calendar.assign.error');
      if (onError) {
        onError(errorMessage);
      } else {
        showNotification('error', errorMessage);
      }
    }
  }, [assignControllerFunction, events, allControllers, selectedControl, t, onError, showNotification]);

  // Helper to show day events dialog
  const showDayEvents = useCallback((date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const dayEventsList = events.filter(e => e.date === dateStr);

    if (dayEventsList.length > 0) {
      // Has events - open day events dialog
      setSelectedDate(date);
      setDayEvents(dayEventsList);
      setDayDialogOpen(true);
    } else if (onCreateControl && canCreateControl && isDateTodayOrFuture(date)) {
      // No events but future date - directly open create control (if permission allows)
      onCreateControl(date);
    }

    if (onDateClick) {
      onDateClick(date, dayEventsList);
    }
  }, [events, onDateClick, onCreateControl, canCreateControl]);

  // Handle date click
  const handleDateClick = (clickInfo) => {
    // Month view: clicking on cell shows events list
    // Week/Day views: all-day area click shows events list, time slot click creates control
    if (view === 'dayGridMonth' || clickInfo.allDay) {
      showDayEvents(clickInfo.date);
    } else if (onCreateControl && canCreateControl && isDateTodayOrFuture(clickInfo.date)) {
      // Week/Day view time slot click - directly create control with specific time (if permission allows)
      onCreateControl(clickInfo.date);
    }
  };

  // Handle day header click (for week/day views)
  const handleNavLinkDayClick = (date) => {
    showDayEvents(date);
  };

  // Handle view change
  const handleViewChange = (viewName) => {
    setView(viewName);
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(viewName);
    }
  };

  // Handle selection change from Select component
  // Navigate prev/next
  const navigateCalendar = (direction) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (direction === 'prev') {
        calendarApi.prev();
      } else {
        calendarApi.next();
      }
      setCurrentDate(calendarApi.getDate());
    }
  };

  // Go to specific month/year
  const goToMonthYear = (month, year) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const newDate = new Date(year, month, 1);
      calendarApi.gotoDate(newDate);
      setCurrentDate(newDate);
    }
  };

  // Go to today
  const goToToday = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  // Export day events to CSV
  const handleExportCSV = () => {
    if (dayEvents.length === 0) return;

    // Use custom export function if provided
    if (exportCSVFunction) {
      exportCSVFunction(dayEvents, {
        t,
        selectedDate,
        isEventPast,
        isEventCompleted,
        CONTROL_STATUS,
        moment
      });
      return;
    }

    // Default export with common data (for BF)
    const csvContent = [
      [
        t('calendar.export.date'),
        t('calendar.export.controller'),
        t('calendar.export.location'),
        t('calendar.export.conformity')
      ].join(','),
      ...dayEvents.map(event => {
        const controllerName = event.controller
          ? `${event.controller.firstName} ${event.controller.lastName}`
          : t('calendar.unassigned');
        const locationName = event.location?.name || 'N/A';
        const dateTime = event.dateTime
          ? moment(event.dateTime).format('DD/MM/YYYY HH:mm')
          : moment(event.date).format('DD/MM/YYYY');
        const getConformityLabel = (conformity) => {
          if (conformity === true || conformity === 1) return t('conformity.valid');
          if (conformity === false || conformity === 0) return t('conformity.invalid');
          return t('conformity.waiting-result');
        };
        return [
          dateTime,
          controllerName,
          locationName,
          getConformityLabel(event.conformity)
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inspections_${moment(selectedDate).format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onOpenSettings = async () => {
    setTempControllerColors({ ...controllerColors });
    setSettingsDialogOpen(true);
    // Fetch all controllers (including hidden) when the settings modal
    // opens. The list view's `fetchControllersFunction` defaults to
    // `includeHidden=false`, so the modal needs its own call to pick up
    // the hidden ones too.
    if (fetchControllersFunction) {
      setSettingsLoading(true);
      try {
        const data = await fetchControllersFunction({ includeHidden: true });
        const controllersList = data.controllers || [];
        setAllControllers(controllersList);
        const colorsFromBackend = {};
        const visibilityFromBackend = {};
        controllersList.forEach((controller) => {
          if (controller.color) {
            colorsFromBackend[controller.id] = controller.color;
          }
          visibilityFromBackend[controller.id] = controller.calendarVisible !== false;
        });
        setTempControllerColors((prev) => ({ ...colorsFromBackend, ...prev }));
        setTempControllerVisibility(visibilityFromBackend);
        setControllerColors((prev) => ({ ...colorsFromBackend, ...prev }));
      } catch (err) {
        console.error('Error fetching controllers:', err);
      } finally {
        setSettingsLoading(false);
      }
    }
  };

  return (
    <ShadCard>
      <ShadCardContent>
        {/* Title + action row */}
        <div className="mb-4 flex items-center justify-between gap-2">
          {title ? (
            <h2 className="text-base font-semibold">{title}</h2>
          ) : (
            <span />
          )}
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-0.5">
              {onImportCalendar ? (
                <ShadTooltip>
                  <TooltipTrigger asChild>
                    <ShadButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={onImportCalendar}
                    >
                      <DownloadIcon className="size-4" />
                    </ShadButton>
                  </TooltipTrigger>
                  <TooltipContent>{t('calendar.import-calendar')}</TooltipContent>
                </ShadTooltip>
              ) : null}
              {onExportTemplate ? (
                <ShadTooltip>
                  <TooltipTrigger asChild>
                    <ShadButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={onExportTemplate}
                    >
                      <UploadIcon className="size-4" />
                    </ShadButton>
                  </TooltipTrigger>
                  <TooltipContent>{t('calendar.export-template')}</TooltipContent>
                </ShadTooltip>
              ) : null}
              <ShadTooltip>
                <TooltipTrigger asChild>
                  <ShadButton
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onOpenSettings}
                  >
                    <SettingsIcon className="size-4" />
                  </ShadButton>
                </TooltipTrigger>
                <TooltipContent>{t('calendar.settings.title')}</TooltipContent>
              </ShadTooltip>
            </div>
          </TooltipProvider>
        </div>
        {/* Toolbar: view selector + navigation */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* View selector — shadcn ToggleGroup gives a proper
                segmented control with aria-pressed semantics and a
                single shared border. Items override `flex-1` so
                each one sizes to its label (Mês/Semana/Dia fit
                comfortably) instead of splitting the row in three. */}
            <ShadToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={view}
              onValueChange={(next) => {
                if (next) handleViewChange(next);
              }}
            >
              <ShadToggleGroupItem
                value="dayGridMonth"
                className="flex-none px-3"
              >
                {t('calendar.view.month')}
              </ShadToggleGroupItem>
              <ShadToggleGroupItem
                value="timeGridWeek"
                className="flex-none px-3"
              >
                {t('calendar.view.week')}
              </ShadToggleGroupItem>
              <ShadToggleGroupItem
                value="timeGridDay"
                className="flex-none px-3"
              >
                {t('calendar.view.day')}
              </ShadToggleGroupItem>
            </ShadToggleGroup>

            {/* Navigation: prev / month-year picker / next / today */}
            <div className="flex items-center gap-1">
              <ShadButton
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => navigateCalendar('prev')}
              >
                <ChevronLeftIcon className="size-4" />
              </ShadButton>
              <ShadPopover
                open={datePickerOpen}
                onOpenChange={(open) => {
                  if (open) setPickerYear(currentDate.getFullYear());
                  setDatePickerOpen(open);
                }}
              >
                <ShadPopoverTrigger asChild>
                  <ShadButton
                    type="button"
                    variant="ghost"
                    className="min-w-[180px] text-center text-base font-semibold capitalize"
                  >
                    {new Intl.DateTimeFormat(i18n.language || 'en', {
                      month: 'long',
                      year: 'numeric',
                    }).format(currentDate)}
                  </ShadButton>
                </ShadPopoverTrigger>
                <ShadPopoverContent align="center" className="w-72 p-3">
                  {/* Year selector */}
                  <div className="mb-3 flex items-center justify-between">
                    <ShadButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPickerYear((prev) => prev - 1)}
                    >
                      <ChevronLeftIcon className="size-4" />
                    </ShadButton>
                    <span className="text-base font-semibold">{pickerYear}</span>
                    <ShadButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPickerYear((prev) => prev + 1)}
                    >
                      <ChevronRightIcon className="size-4" />
                    </ShadButton>
                  </div>
                  {/* Month grid */}
                  <div className="grid grid-cols-3 gap-1">
                    {Array.from({ length: 12 }, (_, i) =>
                      new Intl.DateTimeFormat(i18n.language || 'en', {
                        month: 'short',
                      }).format(new Date(2000, i, 1)),
                    ).map((month, index) => {
                      const isActive =
                        currentDate.getMonth() === index &&
                        currentDate.getFullYear() === pickerYear;
                      return (
                        <ShadButton
                          key={month}
                          type="button"
                          size="sm"
                          variant={isActive ? 'default' : 'ghost'}
                          className="capitalize"
                          onClick={() => {
                            goToMonthYear(index, pickerYear);
                            setDatePickerOpen(false);
                          }}
                        >
                          {month}
                        </ShadButton>
                      );
                    })}
                  </div>
                </ShadPopoverContent>
              </ShadPopover>
              <ShadButton
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => navigateCalendar('next')}
              >
                <ChevronRightIcon className="size-4" />
              </ShadButton>
              <ShadButton
                type="button"
                variant="outline"
                size="sm"
                onClick={goToToday}
                disabled={moment(currentDate).isSame(moment(), 'day')}
              >
                {t('calendar.today')}
              </ShadButton>
            </div>
          </div>

          {/* Controller filter - shown when caller wired up
              fetchControllersFunction and opted in via showControllerFilter */}
          {fetchControllersFunction && showControllerFilter ? (() => {
            const totalOptions = allControllers.length + 1; // +1 for unassigned
            const isAllSelected =
              selectedControllers.length === 0 ||
              selectedControllers.length === totalOptions;
            const partial =
              selectedControllers.length > 0 &&
              selectedControllers.length < totalOptions;
            const triggerLabel = isAllSelected
              ? t('calendar.filter.all')
              : `${selectedControllers.length} ${t('calendar.filter.selected')}`;
            const toggle = (id) => {
              const next = selectedControllers.includes(id)
                ? selectedControllers.filter((cid) => cid !== id)
                : [...selectedControllers, id];
              handleFilterChange(next);
            };
            const toggleSelectAll = () => {
              if (selectedControllers.length === totalOptions) {
                handleFilterChange([]);
              } else {
                handleFilterChange([
                  ...allControllers.map((c) => c.id),
                  UNASSIGNED_FILTER_ID,
                ]);
              }
            };
            return (
              <ShadPopover
                open={controllerFilterOpen}
                onOpenChange={setControllerFilterOpen}
              >
                <ShadPopoverTrigger asChild>
                  <ShadButton
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-w-[220px] justify-between"
                  >
                    <span className="text-muted-foreground">
                      {t('calendar.filter.controllers')}:
                    </span>
                    <ShadBadge variant="secondary" className="ml-2">
                      {triggerLabel}
                    </ShadBadge>
                  </ShadButton>
                </ShadPopoverTrigger>
                <ShadPopoverContent align="start" className="w-72 p-0">
                  <ShadCommand>
                    <ShadCommandInput
                      placeholder={t('calendar.filter.controllers')}
                    />
                    <ShadCommandList>
                      <ShadCommandEmpty>
                        {t('common.no-data', 'No data')}
                      </ShadCommandEmpty>
                      <ShadCommandGroup>
                        <ShadCommandItem
                          onSelect={toggleSelectAll}
                          className="cursor-pointer"
                        >
                          <ShadCheckbox
                            className="mr-2"
                            checked={
                              partial
                                ? 'indeterminate'
                                : selectedControllers.length === totalOptions
                            }
                          />
                          {t('calendar.filter.select-all')}
                        </ShadCommandItem>
                      </ShadCommandGroup>
                      <ShadCommandSeparator />
                      <ShadCommandGroup>
                        <ShadCommandItem
                          value={t('calendar.unassigned')}
                          onSelect={() => toggle(UNASSIGNED_FILTER_ID)}
                          className="cursor-pointer"
                        >
                          <ShadCheckbox
                            className="mr-2"
                            checked={selectedControllers.includes(
                              UNASSIGNED_FILTER_ID,
                            )}
                          />
                          <span
                            aria-hidden
                            className="mr-2 inline-block size-4 rounded"
                            style={{ backgroundColor: UNASSIGNED_COLOR }}
                          />
                          {t('calendar.unassigned')}
                        </ShadCommandItem>
                      </ShadCommandGroup>
                      <ShadCommandSeparator />
                      <ShadCommandGroup>
                        {allControllers.map((controller) => {
                          const label = `${controller.firstName} ${controller.lastName}`;
                          return (
                            <ShadCommandItem
                              key={controller.id}
                              value={label}
                              onSelect={() => toggle(controller.id)}
                              className="cursor-pointer"
                            >
                              <ShadCheckbox
                                className="mr-2"
                                checked={selectedControllers.includes(
                                  controller.id,
                                )}
                              />
                              <span
                                aria-hidden
                                className="mr-2 inline-block size-4 rounded"
                                style={{
                                  backgroundColor:
                                    controllerColors[controller.id] ||
                                    UNASSIGNED_COLOR,
                                }}
                              />
                              {label}
                            </ShadCommandItem>
                          );
                        })}
                      </ShadCommandGroup>
                    </ShadCommandList>
                  </ShadCommand>
                </ShadPopoverContent>
              </ShadPopover>
            );
          })() : null}
        </div>

        {/* Controller legend - shows controllers from current events */}
        {controllers.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1">
            {controllers.map((controller) => {
              const totalOptions = allControllers.length + 1;
              const noFilter =
                selectedControllers.length === 0 ||
                selectedControllers.length === totalOptions;
              const isActive =
                noFilter || selectedControllers.includes(controller.id);
              return (
                <ShadBadge
                  key={controller.id}
                  className={shadCn(
                    'text-white',
                    !isActive && 'opacity-40',
                  )}
                  style={{
                    backgroundColor:
                      controllerColors[controller.id] || UNASSIGNED_COLOR,
                  }}
                >
                  {controller.firstName} {controller.lastName}
                </ShadBadge>
              );
            })}
          </div>
        )}

        {/* Calendar */}
        <div
          className="inspections-calendar-host relative"
          style={{ height, ...calendarSx }}
        >
          {loading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
              <Loader2Icon className="size-7 animate-spin text-muted-foreground" />
            </div>
          ) : null}
          <FullCalendar
            key={`${i18n.language}-${initialViewRef.current}-${initialDateRef.current?.toISOString()}`}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={view}
            initialDate={currentDate}
            headerToolbar={false}
            events={calendarEvents}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            editable={!!updateEventDateFunction && canRescheduleEvents}
            eventStartEditable={!!updateEventDateFunction && canRescheduleEvents}
            eventDurationEditable={false}
            eventDrop={handleEventDrop}
            navLinks
            navLinkDayClick={handleNavLinkDayClick}
            height={height}
            locale={getFullCalendarLocale()}
            firstDay={1} // Monday
            eventDisplay="block"
            dayMaxEvents={3}
            moreLinkClick={(info) => {
              // Open our custom modal instead of the default popover
              showDayEvents(info.date);
              return 'none'; // Prevent default popover
            }}
            displayEventEnd={false}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false
            }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false
            }}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            scrollTime="08:00:00"
            dayHeaderFormat={{
              weekday: 'short',
              day: 'numeric',
              omitCommas: true
            }}
          />
        </div>

        {/* Day events dialog */}
        <Dialog
          open={dayDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setDayDialogOpen(false);
              setExpandedEventId(null);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-8">
                <DialogTitle>
                  {t('calendar.day-events.title', { date: moment(selectedDate).format('DD MMMM YYYY') })}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('calendar.day-events.title', { date: moment(selectedDate).format('DD MMMM YYYY') })}
                </DialogDescription>
                <TooltipProvider delayDuration={300}>
                  <div className="flex items-center gap-0.5">
                    {onCreateControl && canCreateControl && isDateTodayOrFuture(selectedDate) ? (
                      <ShadTooltip>
                        <TooltipTrigger asChild>
                          <ShadButton
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onCreateControl(selectedDate)}
                          >
                            <PlusIcon className="size-4" />
                          </ShadButton>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('calendar.day-events.create-control')}
                        </TooltipContent>
                      </ShadTooltip>
                    ) : null}
                    <ShadTooltip>
                      <TooltipTrigger asChild>
                        <ShadButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleExportCSV}
                          disabled={dayEvents.length === 0}
                        >
                          <DownloadIcon className="size-4" />
                        </ShadButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('calendar.day-events.export-csv')}
                      </TooltipContent>
                    </ShadTooltip>
                  </div>
                </TooltipProvider>
              </div>
            </DialogHeader>
          <div className="mt-4">
            {dayEvents.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                {t('calendar.day-events.no-events')}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {dayEvents.flatMap((event, eventIndex) => {
                  // For aggregated events with multiple vehicles, show each vehicle separately
                  const hasMultipleVehicles = event.vehicles && event.vehicles.length > 1;

                  if (hasMultipleVehicles) {
                    return event.vehicles.map((vehicle, vehicleIndex) => {
                      const vehicleEventId = `${event.id}-${vehicle.controlId || vehicleIndex}`;
                      const isExpanded = expandedEventId === vehicleEventId;
                      // Create a control object for this specific vehicle
                      const vehicleControl = {
                        ...event,
                        controlId: vehicle.controlId,
                        resource: { name: vehicle.resourceName },
                        resourceType: { name: vehicle.resourceType },
                        conformity: vehicle.conformity,
                        status: vehicle.status,
                        vehicleCount: 1
                      };
                      const eventDate = vehicle.dateTime || event.dateTime || event.date;
                      const isCanceled = vehicle.status === CONTROL_STATUS.CANCELED;
                      const isOverdue = isEventPast(eventDate) && !isEventCompleted(vehicle.conformity) && !isCanceled;

                      return (
                        <li key={vehicleEventId}>
                          <button
                            type="button"
                            onClick={() => {
                              if (renderControlDetails) {
                                setExpandedEventId(isExpanded ? null : vehicleEventId);
                              } else {
                                setDayDialogOpen(false);
                                if (onControlClick) onControlClick(vehicleControl);
                              }
                            }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50"
                            style={{
                              backgroundColor: isExpanded
                                ? 'var(--accent)'
                                : isOverdue
                                  ? hexToRgba(OVERDUE_COLOR, 0.1)
                                  : 'transparent',
                            }}
                          >
                            <div className="flex-1">
                              {renderEventListItem
                                ? renderEventListItem(vehicleControl, { controllerColors, t, UNASSIGNED_COLOR }).primary
                                : (
                                  <div className="flex items-center gap-2">
                                    <span
                                      aria-hidden
                                      className="inline-block size-4 rounded"
                                      style={{
                                        backgroundColor: event.controller
                                          ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR)
                                          : UNASSIGNED_COLOR,
                                      }}
                                    />
                                    <span className="text-sm font-medium">
                                      {event.controller
                                        ? `${event.controller.firstName} ${event.controller.lastName}`
                                        : t('calendar.unassigned')}
                                    </span>
                                  </div>
                                )}
                              <div className="mt-1 text-sm text-muted-foreground">
                                {renderEventListItem
                                  ? renderEventListItem(vehicleControl, { controllerColors, t, UNASSIGNED_COLOR }).secondary
                                  : (vehicle.resourceName || 'N/A')}
                              </div>
                            </div>
                            {renderControlDetails ? (
                              isExpanded ? (
                                <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" />
                              ) : (
                                <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                              )
                            ) : null}
                          </button>
                          {renderControlDetails && isExpanded ? (
                            <div
                              className="px-4 pb-4 pt-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {renderControlDetails(vehicleControl, {
                                onClose: () => setExpandedEventId(null),
                                onViewFullDetails: () => {
                                  if (onControlClick) onControlClick(vehicleControl);
                                },
                                onCancel: isOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(vehicleControl) : null,
                                onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(vehicleControl, controllerId) : null,
                                controllers: allControllers,
                                controllerColors,
                                mainColor,
                                inline: true,
                                isOverdue,
                              })}
                            </div>
                          ) : null}
                        </li>
                      );
                    });
                  }

                  // Single vehicle or CATP-style event - expandable
                  const isExpanded = expandedEventId === event.id;
                  const eventDate = event.dateTime || event.date;
                  const isCanceled = event.status === CONTROL_STATUS.CANCELED;
                  const isOverdue = isEventPast(eventDate) && !isEventCompleted(event.conformity) && !isCanceled;
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (renderControlDetails) {
                            setExpandedEventId(isExpanded ? null : event.id);
                          } else {
                            setDayDialogOpen(false);
                            if (onControlClick) onControlClick(event);
                          }
                        }}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50"
                        style={{
                          backgroundColor: isExpanded
                            ? 'var(--accent)'
                            : isOverdue
                              ? hexToRgba(OVERDUE_COLOR, 0.1)
                              : 'transparent',
                        }}
                      >
                        <div className="flex-1">
                          {renderEventListItem
                            ? renderEventListItem(event, { controllerColors, t, UNASSIGNED_COLOR }).primary
                            : (
                              <div className="flex items-center gap-2">
                                <span
                                  aria-hidden
                                  className="inline-block size-4 rounded"
                                  style={{
                                    backgroundColor: event.controller
                                      ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR)
                                      : UNASSIGNED_COLOR,
                                  }}
                                />
                                <span className="text-sm font-medium">
                                  {event.controller
                                    ? `${event.controller.firstName} ${event.controller.lastName}`
                                    : t('calendar.unassigned')}
                                </span>
                              </div>
                            )}
                          <div className="mt-1 text-sm text-muted-foreground">
                            {renderEventListItem
                              ? renderEventListItem(event, { controllerColors, t, UNASSIGNED_COLOR }).secondary
                              : (event.resource?.name || 'N/A')}
                          </div>
                        </div>
                        {renderControlDetails ? (
                          isExpanded ? (
                            <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                          )
                        ) : null}
                      </button>
                      {renderControlDetails && isExpanded ? (
                        <div
                          className="px-4 pb-4 pt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderControlDetails(event, {
                            onClose: () => setExpandedEventId(null),
                            onViewFullDetails: () => {
                              if (onControlClick) onControlClick(event);
                            },
                            onCancel: isOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(event) : null,
                            onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(event, controllerId) : null,
                            controllers: allControllers,
                            controllerColors,
                            mainColor,
                            inline: true,
                            isOverdue,
                          })}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          </DialogContent>
        </Dialog>

        {/* Control Details Modal - uses custom render function */}
        {renderControlDetails && (
          <Dialog
            open={controlDialogOpen}
            onOpenChange={(open) => { if (!open) handleCloseControlDetails(); }}
          >
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {t('calendar.control-details.title')}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('calendar.control-details.title')}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                {selectedControl && (() => {
                  const controlDate = selectedControl.dateTime || selectedControl.date;
                  const controlIsOverdue = isEventPast(controlDate) && !isEventCompleted(selectedControl.conformity);
                  return renderControlDetails(selectedControl, {
                    onClose: handleCloseControlDetails,
                    onViewFullDetails: handleViewFullDetails,
                    onCancel: controlIsOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(selectedControl) : null,
                    onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(selectedControl, controllerId) : null,
                    controllers: allControllers,
                    controllerColors,
                    mainColor,
                    isOverdue: controlIsOverdue,
                  });
                })()}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Month/Year Picker Popover now lives inline next to the
            month title in the toolbar (see ShadPopover above). */}

        {/* Settings Dialog */}
        <Dialog
          open={settingsDialogOpen}
          onOpenChange={(open) => { if (!open) setSettingsDialogOpen(false); }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {t('calendar.settings.title')}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t('calendar.settings.title')}
              </DialogDescription>
            </DialogHeader>
          <div className="mt-4">
            {settingsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2Icon className="size-7 animate-spin text-muted-foreground" />
              </div>
            ) : (allControllers.length > 0 ? allControllers : controllers).length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('calendar.settings.color-matching.no-controllers')}
              </p>
            ) : (() => {
              const controllersForVisibility = allControllers.length > 0 ? allControllers : controllers;
              const controllersForColors = updateControllerVisibilityFunction
                ? controllersForVisibility.filter(
                    (controller) => tempControllerVisibility[controller.id] !== false,
                  )
                : controllersForVisibility;

              const colorContent = controllersForColors.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t('calendar.settings.color-matching.no-visible-controllers', 'No visible controllers to configure.')}
                </p>
              ) : (
                <ShadScrollArea
                  className="pr-2"
                  style={{ height: updateControllerVisibilityFunction ? 250 : 300 }}
                >
                  {controllersForColors.map((controller) => (
                    <div
                      key={controller.id}
                      className="flex items-center justify-between gap-3 border-b border-border py-2 px-2 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          data-color-preview
                          aria-hidden
                          className="inline-block size-6 rounded border border-border"
                          style={{
                            backgroundColor:
                              tempControllerColors[controller.id] || UNASSIGNED_COLOR,
                          }}
                        />
                        <span className="text-sm">
                          {controller.firstName} {controller.lastName}
                        </span>
                      </div>
                      <input
                        type="color"
                        defaultValue={tempControllerColors[controller.id] || UNASSIGNED_COLOR}
                        onBlur={(e) => {
                          setTempControllerColors((prev) => ({
                            ...prev,
                            [controller.id]: e.target.value,
                          }));
                        }}
                        onChange={(e) => {
                          const previewBox =
                            e.target.parentElement?.querySelector('[data-color-preview]');
                          if (previewBox) {
                            previewBox.style.backgroundColor = e.target.value;
                          }
                        }}
                        className="h-7 w-10 cursor-pointer rounded border border-border p-0"
                      />
                    </div>
                  ))}
                </ShadScrollArea>
              );

              return updateControllerVisibilityFunction ? (
                <ShadAccordion type="multiple" defaultValue={['visibility']} className="space-y-2">
                  <ShadAccordionItem value="visibility" className="rounded-md border border-border px-3">
                    <ShadAccordionTrigger className="text-sm font-semibold">
                      {t('calendar.settings.visibility.title', 'Controller Visibility')}
                    </ShadAccordionTrigger>
                    <ShadAccordionContent>
                      <p className="mb-3 text-sm text-muted-foreground">
                        {t('calendar.settings.visibility.description', 'Choose which controllers appear in the calendar filters.')}
                      </p>
                      <ShadScrollArea className="h-[250px] pr-2">
                        {controllersForVisibility.map((controller) => (
                          <div
                            key={controller.id}
                            className="flex items-center justify-between gap-3 border-b border-border py-1.5 px-2 last:border-b-0"
                          >
                            <span className="text-sm">
                              {controller.firstName} {controller.lastName}
                            </span>
                            <ShadSwitch
                              checked={tempControllerVisibility[controller.id] !== false}
                              onCheckedChange={(checked) => {
                                setTempControllerVisibility((prev) => ({
                                  ...prev,
                                  [controller.id]: checked,
                                }));
                              }}
                            />
                          </div>
                        ))}
                      </ShadScrollArea>
                    </ShadAccordionContent>
                  </ShadAccordionItem>
                  <ShadAccordionItem value="colors" className="rounded-md border border-border px-3">
                    <ShadAccordionTrigger className="text-sm font-semibold">
                      {t('calendar.settings.color-matching.title')}
                    </ShadAccordionTrigger>
                    <ShadAccordionContent>
                      <p className="mb-3 text-sm text-muted-foreground">
                        {t('calendar.settings.color-matching.description')}
                      </p>
                      {colorContent}
                    </ShadAccordionContent>
                  </ShadAccordionItem>
                </ShadAccordion>
              ) : (
                <>
                  <h3 className="mb-3 text-sm font-semibold">
                    {t('calendar.settings.color-matching.title')}
                  </h3>
                  <p className="mb-3 text-sm text-muted-foreground">
                    {t('calendar.settings.color-matching.description')}
                  </p>
                  {colorContent}
                </>
              );
            })()}

            <ShadSeparator className="my-4" />

            <div className="flex justify-end gap-2">
              <ShadButton
                type="button"
                variant="outline"
                onClick={() => setSettingsDialogOpen(false)}
                disabled={savingColors}
              >
                {t('common.cancel')}
              </ShadButton>
              <ShadButton
                type="button"
                disabled={savingColors}
                onClick={async () => {
                  setSavingColors(true);
                  try {
                    const controllersToUpdate = allControllers.length > 0 ? allControllers : controllers;
                    const savePromises = [];
                    if (updateControllerVisibilityFunction) {
                      controllersToUpdate
                        .filter((controller) => {
                          const currentVisible = controller.calendarVisible !== false;
                          const newVisible = tempControllerVisibility[controller.id] !== false;
                          return currentVisible !== newVisible;
                        })
                        .forEach((controller) => {
                          savePromises.push(
                            updateControllerVisibilityFunction(
                              controller.id,
                              tempControllerVisibility[controller.id] !== false,
                            ),
                          );
                        });
                    }
                    if (updateControllerColorFunction) {
                      controllersToUpdate
                        .filter(
                          (controller) =>
                            tempControllerColors[controller.id] &&
                            tempControllerColors[controller.id] !== controller.color,
                        )
                        .forEach((controller) => {
                          savePromises.push(
                            updateControllerColorFunction(
                              controller.id,
                              tempControllerColors[controller.id],
                            ),
                          );
                        });
                    }
                    await Promise.all(savePromises);
                    if (fetchControllersFunction) {
                      const data = await fetchControllersFunction();
                      const visibleControllers = data.controllers || [];
                      setAllControllers(visibleControllers);
                      allControllersCountRef.current = visibleControllers.length;
                      const visibleControllerIds = new Set(
                        visibleControllers.map((c) => c.id),
                      );
                      const updatedSelection = selectedControllersRef.current.filter(
                        (id) =>
                          id === UNASSIGNED_FILTER_ID || visibleControllerIds.has(id),
                      );
                      if (updatedSelection.length !== selectedControllersRef.current.length) {
                        selectedControllersRef.current = updatedSelection;
                        setSelectedControllers(updatedSelection);
                      }
                      setFilterTrigger((prev) => prev + 1);
                    }
                  } catch (err) {
                    console.error('Error saving settings:', err);
                  } finally {
                    setSavingColors(false);
                  }
                  setControllerColors(tempControllerColors);
                  setSettingsDialogOpen(false);
                }}
              >
                {savingColors ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  t('common.confirm')
                )}
              </ShadButton>
            </div>
          </div>
          </DialogContent>
        </Dialog>

        {/* Snackbar/Alert moved to the global sonner toaster — see
            useNotificationsProvider().showNotification calls above. */}
      </ShadCardContent>
    </ShadCard>
  );
}
